import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { isAbortError, useBoundedRequest } from "@/hooks/use-bounded-request";
import { useLatestOnly } from "@/hooks/use-latest-only";
import { usePollWhile } from "@/hooks/use-poll-while";

/** The in-flight runs tabs' poll cadence. */
export const POLL_INTERVAL_MS = 3000;
/** Hard deadline on any single runs request — see `use-bounded-request`. Ten poll cycles, matching the
 *  recurring page's bound. [code review r3] */
export const STALE_REQUEST_MS = POLL_INTERVAL_MS * 10;

/**
 * One runs tab's list: the fetch on mount, the visible reload after a row action, and the silent poll tick —
 * every one bounded by {@link STALE_REQUEST_MS} and ordered by {@link useLatestOnly}. The tabs own only their
 * columns and dialogs. `pollWhile` decides, from the committed value, whether the tab keeps polling.
 */
export function useRunList<R>(
  fetcher: (signal?: AbortSignal) => Promise<R>,
  initial: R,
  pollWhile?: (value: R) => boolean
): { value: R; isLoading: boolean; load: () => Promise<void> } {
  const [value, setValue] = useState<R>(initial);
  const [isLoading, setIsLoading] = useState(true);

  // Latest-fetcher ref: the prop is a fresh arrow on every parent render, and the tab fetches once per mount
  // (Radix remounts it on re-activation).
  const fetcherRef = useRef(fetcher);
  useEffect(() => {
    fetcherRef.current = fetcher;
  }, [fetcher]);

  // Mount, the post-action reload and the poll tick all fetch the same list; only the newest may commit.
  const latest = useLatestOnly();
  // …and every one of them is bounded, so a hung request settles instead of accumulating. [code review r3]
  const { run } = useBoundedRequest(STALE_REQUEST_MS);

  /** Visible fetch: skeleton + error toast. Mount and post-action reload. */
  const load = useCallback(async () => {
    const token = latest.begin();
    setIsLoading(true);
    try {
      const next = await run((signal) => fetcherRef.current(signal));
      if (latest.claimCommit(token)) setValue(next);
    } catch (err) {
      if (latest.isCurrent(token) && !isAbortError(err)) {
        toast.error(err instanceof Error ? err.message : "Failed to load runs");
      }
    } finally {
      if (latest.isCurrent(token)) setIsLoading(false);
    }
  }, [latest, run]);

  /** Silent fetch for polling: no skeleton (DataTableWrapper would swap it in every tick) and no toast (a
   *  failing poll must not spam). */
  const refresh = useCallback(async () => {
    const token = latest.begin();
    try {
      const next = await run((signal) => fetcherRef.current(signal));
      // A tick that captured the pre-action list must not commit after the post-action reload.
      if (latest.claimCommit(token)) setValue(next);
    } catch {
      // Intentionally quiet — the visible load() is what reports failures.
    } finally {
      // The current writer owns the loading flag too, on EVERY path. A tick that outranks a post-action
      // load() makes that load stale, so its `finally` skips setIsLoading(false); if this refresh then
      // rejects — a 500, or its own request deadline — a success-only release would leave
      // DataTableWrapper on its skeleton for the rest of the mount. [code review r3, completed r4]
      if (latest.isCurrent(token)) setIsLoading(false);
    }
  }, [latest, run]);

  useEffect(() => {
    void load();
  }, [load]);

  usePollWhile({
    enabled: pollWhile?.(value) ?? false,
    intervalMs: POLL_INTERVAL_MS,
    onTick: refresh,
  });

  return { value, isLoading, load };
}
